import { useState } from "react";
import type { FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/dah86fjp14a1h3mr6f10";
const MAX_MESSAGE = 500;

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      setMessageLength(0);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = responseText ? (JSON.parse(responseText) as FormResponse) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? responseText ?? "";
      const isSpam = /spam/i.test(String(serverMsg)) || /spam/i.test(responseText);

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
        setMessageLength(0);
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试，或者稍后再试一次。");
      }
    } catch {
      setStatus("error");
      setFormError("网络连接异常，请检查网络后重试。");
    }
  };

  if (status === "success") {
    return (
      <div className="flex flex-col items-center rounded-card border border-secondary-200 bg-secondary-50 px-6 py-16 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-500 text-background-50">
          <i className="ri-check-line text-3xl"></i>
        </span>
        <h2 className="mt-5 font-heading text-xl font-bold text-foreground-950">留言已收到</h2>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
          感谢你的反馈。编辑团队会在一个工作日内查看并回复到你填写的邮箱，如果涉及内容纠错，我们会在核对后直接修订原文。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-secondary-300 px-5 py-2.5 text-sm font-medium text-secondary-800 transition-colors duration-200 hover:bg-secondary-100"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-edit-2-line text-base"></i>
          </span>
          再写一条留言
        </button>
      </div>
    );
  }

  const inputClass =
    "w-full rounded-xl border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors duration-200 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-card border border-background-200/80 bg-background-50 p-5 md:p-7"
    >
      <h2 className="font-heading text-lg font-bold text-foreground-950">给我们留言</h2>
      <p className="mt-2 text-sm text-foreground-600">
        内容纠错、选题建议或合作咨询都可以在这里提交，带 <span className="text-primary-600">*</span> 的为必填项。
      </p>

      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">
            你的称呼 <span className="text-primary-600">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            maxLength={40}
            autoComplete="name"
            placeholder="怎么称呼你"
            className={inputClass}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">
            邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="用于接收回复的邮箱"
            className={inputClass}
          />
        </div>

        <div className="flex flex-col gap-2 sm:col-span-2">
          <label htmlFor="contact-topic" className="text-sm font-medium text-foreground-800">
            咨询类型 <span className="text-primary-600">*</span>
          </label>
          <select id="contact-topic" name="topic" required defaultValue="内容纠错" className={inputClass}>
            <option value="内容纠错">内容纠错</option>
            <option value="选题建议">选题建议</option>
            <option value="线索投稿">线索投稿</option>
            <option value="合作咨询">合作咨询</option>
            <option value="其他问题">其他问题</option>
          </select>
        </div>

        <div className="flex flex-col gap-2 sm:col-span-2">
          <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">
            留言内容 <span className="text-primary-600">*</span>
          </label>
          <textarea
            id="contact-message"
            name="message"
            required
            rows={5}
            maxLength={MAX_MESSAGE}
            onChange={(e) => setMessageLength(e.target.value.length)}
            placeholder="请描述具体情况，如果是内容纠错，建议附上话题标题与需要修正的地方"
            className={`${inputClass} resize-none`}
          ></textarea>
          <div className="flex items-center justify-between text-xs text-foreground-500">
            <span>最多 {MAX_MESSAGE} 字</span>
            <span className={messageLength > MAX_MESSAGE * 0.9 ? "text-primary-600" : ""}>
              {messageLength}/{MAX_MESSAGE}
            </span>
          </div>
        </div>
      </div>

      <div className="contact-alt-field" aria-hidden="true">
        <label htmlFor="contact-website-alt">网址（请勿填写）</label>
        <input
          id="contact-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {status === "error" && formError && (
        <p className="mt-5 flex items-start gap-2 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-error-warning-line text-base"></i>
          </span>
          {formError}
        </p>
      )}

      <div className="mt-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-foreground-500">
          提交即表示你同意我们仅将信息用于本次问题回复，不会用于其他用途。
        </p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={`${status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} text-base`}></i>
          </span>
          {status === "submitting" ? "正在提交…" : "提交留言"}
        </button>
      </div>
    </form>
  );
}